import React, { MutableRefObject, useState, useEffect } from "react";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Logo from "../../../assets/logo-svg.svg";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DoneRoundedIcon from "@mui/icons-material/DoneRounded";
import ShowInputIcon from "../../../assets/ShowInputIcon.svg";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import FolderOpenRoundedIcon from "@mui/icons-material/FolderOpenRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import { useDispatch, useSelector } from "react-redux";
import { setAssetType } from "../../../redux/features/appState";
import { RootState } from "../../../redux/store";
import { showToast } from "../../../redux/features/toast/toast.slice";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../axiosInstance";
import * as d3 from "d3";
import jsPDF from "jspdf";
import debounce from "lodash.debounce";
import { useLocation } from "react-router-dom";
import { store } from "../../../redux/store";
import { exportToJsonFile } from "../../../utils/downloadJson";
import { unscaleCoordinate } from "../hooks/useFloorPlan";
import { Label, Room, Wall } from "./Floorplan";
import { GuidedTour } from "../../inputGraphEditor/components/GuidedTour";
import { outputEditorTourSteps } from "../outputEditorTour";

interface TopNavProps {
  svgRef: MutableRefObject<SVGSVGElement | null>;
}

const NEW_INPUT_SESSION_KEY = "newInputEditorState";

const hasNonEmptyArray = (value: any) => Array.isArray(value) && value.length > 0;

const hasNonEmptyObject = (value: any) =>
  value && typeof value === "object" && !Array.isArray(value) && Object.keys(value).length > 0;

const hasMeaningfulNewInputState = (value: any) => {
  if (!value || typeof value !== "object") return false;

  if (hasNonEmptyArray(value.rooms)) return true;
  if (hasNonEmptyArray(value.fixedRooms)) return true;
  if (hasNonEmptyArray(value.adjacencyPairs)) return true;
  if (hasNonEmptyArray(value.nonAdjacencyPairs)) return true;
  if (hasNonEmptyArray(value.canvasLines)) return true;
  if (hasNonEmptyArray(value.floorplans)) return true;
  if (hasNonEmptyArray(value.apiBoundary)) return true;
  if (hasNonEmptyObject(value.idToNameMap)) return true;
  if (value.spaceResult || value.gaResult) return true;

  return false;
};

const TopNav: React.FC<TopNavProps> = ({ svgRef }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [exportAnchorEl, setExportAnchorEl] = useState<null | HTMLElement>(null);
  const [isComponentVisible, setComponentVisible] = useState(false);
  const [isRenameMode, setRenameMode] = useState(false);
  const [documentTitle, setDocumentTitle] = useState("Untitled Document 1");
  const dispatch = useDispatch();
  const plot_boundary = useSelector(
    (state: RootState) => state.outputEditorButtonState.plot_boundary,
  );
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleExportOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setExportAnchorEl(event.currentTarget);
  };
  const handleExportClose = () => {
    setExportAnchorEl(null);
  };
  const navigate = useNavigate();
  const location = useLocation();
  const outputOriginPath = location.state?.originPath;
  const routeDocumentId = location.state?.documentId;
  const routeDocumentTitle = location.state?.documentTitle;
  const routeNewInputSavedData = location.state?.newInputSavedData;
  const readSessionNewInputState = () => {
    // If we specifically opened from saved documents, never read from transient session storage.
    // Otherwise (e.g. from newinputeditor, or if undefined due to page reload), we can attempt to read it.
    if (outputOriginPath === "/saved-documents") return null;
    
    const raw = sessionStorage.getItem(NEW_INPUT_SESSION_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (error) {
      console.warn("Failed to parse new input editor session state:", error);
      return null;
    }
  };
  const sessionNewInputState = readSessionNewInputState();
  const resolvedNewInputSavedData = hasMeaningfulNewInputState(
    routeNewInputSavedData,
  )
    ? routeNewInputSavedData
    : hasMeaningfulNewInputState(sessionNewInputState)
      ? sessionNewInputState
      : undefined;
  const canReturnToNewInput = Boolean(resolvedNewInputSavedData);
  const backToInputPath =
    outputOriginPath === "/newinputeditor" ? "/newinputeditor" : "/inputeditor";
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  let plotWidth = useSelector((state: any) => state.nodesState.plotWidth);
  let plotHeight = useSelector((state: any) => state.nodesState.plotHeight);
  const rooms = useSelector(
    (state: RootState) => state.outputEditorButtonState.rooms,
  );

  useEffect(() => {
    if (routeDocumentTitle) {
      setDocumentTitle(routeDocumentTitle);
    }
  }, [routeDocumentTitle]);

  const selectedFloorPlan = useSelector(
    (state: RootState) => state.graph.selectedFloorPlan,
  );

  const graphData = useSelector((state: RootState) => state.graph.graphData);
  const walls = useSelector(
    (state: RootState) => state.outputEditorButtonState.walls,
  );
  const windowAssets = useSelector(
    (state: RootState) => state.outputEditorButtonState.windowAssets,
  );
  const doorAssets = useSelector(
    (state: RootState) => state.outputEditorButtonState.doorAssets,
  );
  const labels = useSelector(
    (state: RootState) => state.outputEditorButtonState.labels,
  );
  const height = useSelector(
    (state: RootState) => state.outputEditorButtonState.height,
  );
  const width = useSelector(
    (state: RootState) => state.outputEditorButtonState.width,
  );

  const postData = (isGraph: boolean) => {
    const url = `${process.env.REACT_APP_BASEURL}/api/document/`;
    let nodesNew: any[] = [];
    if (graphData) {
      graphData.nodes.forEach((ele) => {
        const height = visualViewport?.height;
        const width = visualViewport?.width;
        if (width && height)
          nodesNew.push({ ...ele, x: ele.x / width, y: ele.y / height });
      });

      let unscaledWalls: Wall[] = [];
      walls?.forEach((wall) => {
        unscaledWalls.push({
          id: wall.id,
          roomIds: wall.roomIds,
          x1: unscaleCoordinate(wall.x1, true, width, height),
          x2: unscaleCoordinate(wall.x2, true, width, height),
          y1: unscaleCoordinate(wall.y1, false, width, height),
          y2: unscaleCoordinate(wall.y2, false, width, height),
        });
      });
      let unscaledRooms: Room[] = [];
      rooms?.forEach((room) => {
        let unscaledWallsOfRooms: Wall[] = [];
        room.walls?.forEach((wall) => {
          unscaledWallsOfRooms.push({
            id: wall.id,
            roomIds: wall.roomIds,
            x1: unscaleCoordinate(wall.x1, true, width, height),
            x2: unscaleCoordinate(wall.x2, true, width, height),
            y1: unscaleCoordinate(wall.y1, false, width, height),
            y2: unscaleCoordinate(wall.y2, false, width, height),
          });
        });
        unscaledRooms.push({ ...room, walls: unscaledWallsOfRooms });
      });
      let unscaledLabels: Label[] = [];
      labels?.forEach((label) => {
        unscaledLabels.push({
          ...label,
          x: unscaleCoordinate(label.x, true, width, height),
          y: unscaleCoordinate(label.y, false, width, height),
        });
      });
      let unscaledDoors:
        | Array<{ id: string; x: number; y: number; isVertical: boolean }>
        | null
        | undefined = [];
      doorAssets?.forEach((door) => {
        unscaledDoors?.push({
          ...door,
          x: unscaleCoordinate(door.x, true, width, height),
          y: unscaleCoordinate(door.y, false, width, height),
        });
      });
      let unscaledWindows:
        | Array<{ id: string; x: number; y: number; isVertical: boolean }>
        | null
        | undefined = [];
      windowAssets?.forEach((window) => {
        unscaledWindows?.push({
          ...window,
          x: unscaleCoordinate(window.x, true, width, height),
          y: unscaleCoordinate(window.y, false, width, height),
        });
      });

      let unscaledPlotBoundary: Wall[] = [];
      plot_boundary?.forEach((wall) => {
        unscaledPlotBoundary.push({
          id: wall.id,
          roomIds: wall.roomIds,
          x1: unscaleCoordinate(wall.x1, true, width, height),
          x2: unscaleCoordinate(wall.x2, true, width, height),
          y1: unscaleCoordinate(wall.y1, false, width, height),
          y2: unscaleCoordinate(wall.y2, false, width, height),
        });
      });
      // --- CORRECTED DATA PAYLOAD ---
      // Always mark output editor saves as FLOORPLAN with /outputeditor origin,
      // so they route back to the output editor (not the new input editor).
      // Preserve new_input_saved_data if available so the "Back to Input" button still works.
      const newInputPayload: Record<string, any> = {
        document_type: "FLOORPLAN",
        editor_origin: "/outputeditor",
        ...(resolvedNewInputSavedData
          ? { new_input_saved_data: resolvedNewInputSavedData }
          : {}),
      };
      const data = {
        name: isGraph ? `${documentTitle} [GRAPH]` : documentTitle,
        floorplans: selectedFloorPlan,
        graph_saved_data: {
          ...graphData,
          nodes: nodesNew,
          plot_width: width || 0,
          plot_height: height || 0,
        },
        // FIX: This key was missing. It contains all the updated floorplan info.
        floorplan_data: {
          walls: unscaledWalls,
          rooms: unscaledRooms,
          windows: unscaledWindows,
          doors: unscaledDoors,
          labels: unscaledLabels,
          plot_boundary: unscaledPlotBoundary,
        },
        ...newInputPayload,
      };

      // This will now log the complete and correct data
      console.log("SENDING DATA TO SERVER: ", data);

      const token = localStorage.getItem("access_token");
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };

      axiosInstance
        .post(url, data, { headers })
        .then((response) => {
          console.log("Save successful:", response);
          dispatch(showToast({ message: "Floorplan saved successfully!", type: "success" }));
        })
        .catch((error) => {
          console.error("Error saving:", error);
          dispatch(showToast({ message: "Failed to save floorplan", type: "error" }));
        });

      // The rest of your local storage logic...
      const fileKey = `${documentTitle} [GRAPH]`;
      let savedGraphData = JSON.parse(
        localStorage.getItem("graphDataMap") || "{}",
      );
      savedGraphData[fileKey] = {
        plotWidth: plotWidth || 0,
        plotHeight: plotHeight || 0,
        edges: graphData.edges || [],
      };
      localStorage.setItem("graphDataMap", JSON.stringify(savedGraphData));

      setAnchorEl(null);
    }
  };

  const goToFiles = () => {
    navigate("/saved-documents", {
      state: {
        fromOutputEditor: true,
        outputOriginPath: backToInputPath,
      },
    });
  };

  const goToInputeditor = () => {
    navigate("/inputeditor");
  };
  const handleComponentButtonClick = () => {
    dispatch(setAssetType("window"));
  };

  const handleRenameClick = () => {
    setRenameMode(true);
  };

  const handleDocumentTitleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setDocumentTitle(event.target.value);
  };

  const handleTitleDoubleClick = () => {
    setRenameMode(true);
  };

  const handleTitleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" && documentTitle !== "") {
      setRenameMode(false);
    }
  };

  const handleTitleComplete = () => {
    if (documentTitle !== "") {
      setRenameMode(false);
    }
  };

  const handleShowInput = () => {
    if (!canReturnToNewInput) return;
    navigate("/newinputeditor", {
      state: {
        from: "Output Editor",
        documentId: routeDocumentId,
        documentTitle: routeDocumentTitle || documentTitle,
        newInputSavedData: resolvedNewInputSavedData,
        timestamp: Date.now(),
      },
    });
  };

  const handleBackToInput = () => {
    if (canReturnToNewInput) {
      handleShowInput();
    } else {
      goToInputeditor();
    }
  };

  function loadImageAsBase64(url: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous"; // To handle cross-origin issues
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        if (!ctx) return reject("Canvas context is not available");
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        const base64Image = canvas.toDataURL("image/png");
        resolve(base64Image);
      };
      img.onerror = (err) => reject(err);
      img.src = url;
    });
  }

  const savePdf = async () => {
    const svgElement = svgRef.current;
    if (!svgElement) return;

    // Select the SVG container and reset zoom
    const svgD = d3
      .select<SVGSVGElement, unknown>(svgElement)
      .attr("width", "100%")
      .attr("height", "100%");

    const mainGroup = svgD.select(".main-group");

    // Reset zoom (Optional)
    const zoomBehavior = d3.zoom<SVGSVGElement, unknown>();
    zoomBehavior.transform(
      svgD as unknown as d3.Selection<SVGSVGElement, unknown, any, any>,
      d3.zoomIdentity,
    );
    mainGroup.attr("transform", null);

    // Create a new jsPDF instance
    const pdf = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "a4",
    });

    const pageWidth = pdf.internal.pageSize.getWidth(); // A4 page width in points
    const pageHeight = pdf.internal.pageSize.getHeight(); // A4 page height in points

    // Get all image elements in the SVG
    const images = svgElement.querySelectorAll("image");

    // Load all images as base64
    const imagePromises = Array.from(images).map((image) => {
      const imageUrl = image.getAttribute("href");
      if (!imageUrl) return Promise.resolve(); // Skip if no href
      return loadImageAsBase64(imageUrl).then((base64Image) => {
        // Replace href with base64 image
        image.setAttribute("href", base64Image);
      });
    });

    // Wait for all images to load and replace href attributes with base64
    await Promise.all(imagePromises);

    // Now convert SVG to a string
    const svgAsString = new XMLSerializer().serializeToString(svgElement);
    const svgBlob = new Blob([svgAsString], { type: "image/svg+xml" });
    const svgUrl = URL.createObjectURL(svgBlob);

    // Create a canvas to render the SVG into
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = () => {
      // Get the SVG bounding box dimensions
      const { width: originalWidth, height: originalHeight } =
        svgElement.getBoundingClientRect();

      // Calculate scale factor to fit SVG to A4 width, maintaining aspect ratio
      const scale = pageWidth / originalWidth;
      const scaledHeight = originalHeight * scale;

      // Set canvas size to match the SVG size
      canvas.width = originalWidth;
      canvas.height = originalHeight;

      if (ctx) {
        // Draw the SVG as an image on the canvas
        ctx.drawImage(img, 0, 0, originalWidth, originalHeight);

        // Convert canvas to PNG image data URI
        const imgData = canvas.toDataURL("image/png");

        // Add the image to the PDF
        pdf.addImage(imgData, "PNG", 0, 0, pageWidth, scaledHeight);
        pdf.save("outputfloorplan.pdf");
      }

      // Clean up the object URL
      URL.revokeObjectURL(svgUrl);
    };

    // Trigger loading of the SVG image
    img.src = svgUrl;
  };

  function exportjson() {
    const floorplanState = store.getState().outputEditorButtonState;
    const width = floorplanState.width;
    const height = floorplanState.height;

    //Safeguard: Stop if these specific dimensions aren't loaded
    if (!width || !height) {
      console.error(
        "Floorplan dimensions from outputEditorButtonState are not loaded. Cannot export.",
      );
      alert(
        "Error: Floor plan dimensions are not loaded yet. Please wait a moment for the plan to render and try again.",
      );
      return;
    }

    const { walls, rooms, labels, doorAssets, windowAssets, plot_boundary } =
      floorplanState;

    // Unscale Walls
    const unscaledWalls: Wall[] = (walls || []).map((wall: Wall) => ({
      id: wall.id,
      roomIds: wall.roomIds,
      x1: unscaleCoordinate(wall.x1, true, width, height),
      x2: unscaleCoordinate(wall.x2, true, width, height),
      y1: unscaleCoordinate(wall.y1, false, width, height),
      y2: unscaleCoordinate(wall.y2, false, width, height),
    }));

    // Unscale Rooms (and their nested walls)
    const unscaledRooms: Room[] = (rooms || []).map((room: Room) => ({
      ...room,
      walls: (room.walls || []).map((wall: Wall) => ({
        id: wall.id,
        roomIds: wall.roomIds,
        x1: unscaleCoordinate(wall.x1, true, width, height),
        x2: unscaleCoordinate(wall.x2, true, width, height),
        y1: unscaleCoordinate(wall.y1, false, width, height),
        y2: unscaleCoordinate(wall.y2, false, width, height),
      })),
    }));

    // Unscale Labels
    const unscaledLabels: Label[] = (labels || []).map((label: Label) => ({
      ...label,
      x: unscaleCoordinate(label.x, true, width, height),
      y: unscaleCoordinate(label.y, false, width, height),
    }));

    // Define a simple type for assets
    type Asset = { id: string; x: number; y: number; isVertical: boolean };

    // Unscale Doors
    const unscaledDoors: Asset[] = (doorAssets || []).map((door: Asset) => ({
      ...door,
      x: unscaleCoordinate(door.x, true, width, height),
      y: unscaleCoordinate(door.y, false, width, height),
    }));

    // Unscale Windows
    const unscaledWindows: Asset[] = (windowAssets || []).map(
      (window: Asset) => ({
        ...window,
        x: unscaleCoordinate(window.x, true, width, height),
        y: unscaleCoordinate(window.y, false, width, height),
      }),
    );

    const dataToExport = {
      rooms: unscaledRooms,
      walls: unscaledWalls,
      labels: unscaledLabels,
      windows: unscaledWindows,
      doors: unscaledDoors,
      plot_width: width || 0, // Use the same dimensions we used for unscaling
      plot_height: height || 0, // Use the same dimensions we used for unscaling
    };

    console.log(
      "EXPORTING UNSCALED JSON (using outputEditorButtonState dimensions):",
      dataToExport,
    );
    exportToJsonFile(dataToExport, "floorplan_data.json");
  }

  return (
    <AppBar
      position="absolute"
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "inherit",
        marginTop: "20px",
        boxShadow: "none",
        cursor: "default",
        zIndex: 1000,
      }}
    >
      <Toolbar
        sx={{
          backgroundColor: "#FBE5C6",
          borderRadius: "6px",
          width: "95%",
          border: "2px solid black",
          display: "flex",
          alignItems: "center",
          padding: "0 16px",
        }}
      >
        {/* Brand logo & GPLAN text - Clickable going to /home */}
        <Box
          onClick={() => navigate("/home")}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            cursor: "pointer",
            backgroundColor: "#FF9A56",
            padding: ".5rem 1rem",
            borderRadius: "6px",
            border: "2px solid black",
            "&:hover": { backgroundColor: "#FFB07A" },
            transition: "all 0.1s ease",
            mr: "12px",
          }}
        >
          <img src={Logo} alt="GPlan" style={{ filter: "brightness(0)" }} />
          <Typography
            sx={{
              color: "black",
              fontFamily: "Montserrat",
              fontSize: 16,
              fontWeight: 700,
              letterSpacing: "0.5px",
            }}
          >
            GPLAN
          </Typography>
        </Box>

        <Divider
          orientation="vertical"
          flexItem
          sx={{
            borderRightWidth: 2,
            borderColor: "black",
            my: 1.5,
            mr: "16px",
          }}
        />
        <Typography
          variant="h6"
          sx={{
            color: "black",
            fontFamily: "Montserrat",
            fontSize: 16,
            fontWeight: 600,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            maxWidth: { xs: "120px", sm: "200px", md: "350px" },
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          onDoubleClick={handleTitleDoubleClick}
        >
          {isRenameMode ? (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <input
                type="text"
                value={documentTitle}
                onChange={handleDocumentTitleChange}
                onKeyPress={handleTitleKeyPress}
                onBlur={handleTitleComplete}
                style={{
                  border: "none",
                  outline: "none",
                  minWidth: "150px",
                  borderBottom: "2px solid black",
                  color: "black",
                  fontFamily: "Montserrat",
                  fontSize: 16,
                  fontWeight: 600,
                  backgroundColor: "transparent",
                }}
              />
              <DoneRoundedIcon
                sx={{
                  marginLeft: "8px",
                  verticalAlign: "middle",
                  cursor: "pointer",
                }}
                onClick={handleTitleComplete}
              />
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {documentTitle}
              </span>
              <EditOutlinedIcon
                sx={{
                  marginLeft: "8px",
                  verticalAlign: "middle",
                  cursor: "pointer",
                  fontSize: 18,
                  flexShrink: 0,
                }}
                onClick={handleTitleDoubleClick}
              />
            </Box>
          )}
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: "4px", sm: "6px", md: "10px" },
            marginLeft: "auto",
          }}
        >
          <Button
            sx={{
              backgroundColor: "#FBE5C6",
              padding: { xs: ".4rem .6rem", md: ".5rem 1rem" },
              borderRadius: "6px",
              border: "2px solid black",
              "&:hover": { backgroundColor: "#FFF4E5" },
              color: "black",
              fontFamily: "Montserrat",
              fontWeight: 600,
              textTransform: "none",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              minWidth: 0,
            }}
            onClick={() => navigate("/home")}
            title="Home"
          >
            <HomeRoundedIcon sx={{ fontSize: 20 }} />
            <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
              Home
            </Box>
          </Button>
          <Button
            sx={{
              backgroundColor: "#FBE5C6",
              padding: { xs: ".4rem .6rem", md: ".5rem 1rem" },
              borderRadius: "6px",
              border: "2px solid black",
              "&:hover": { backgroundColor: "#FFF4E5" },
              color: "black",
              fontFamily: "Montserrat",
              fontWeight: 600,
              textTransform: "none",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              minWidth: 0,
            }}
            onClick={goToFiles}
            title="All Files"
          >
            <FolderOpenRoundedIcon sx={{ fontSize: 20 }} />
            <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
              All Files
            </Box>
          </Button>
          <Button
            sx={{
              backgroundColor: "#FF9A56",
              padding: { xs: ".4rem .6rem", md: ".5rem 1rem" },
              borderRadius: "6px",
              border: "2px solid black",
              "&:hover": { backgroundColor: "#FFB07A" },
              color: "black",
              fontFamily: "Montserrat",
              fontWeight: 600,
              textTransform: "none",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              minWidth: 0,
            }}
            onClick={handleBackToInput}
            title="Back to Input"
          >
            <ArrowBackRoundedIcon sx={{ fontSize: 20 }} />
            <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
              Back to Input
            </Box>
          </Button>
          <Button
            sx={{
              backgroundColor: "#FF9A56",
              padding: { xs: ".4rem .6rem", md: ".5rem 1rem" },
              borderRadius: "6px",
              border: "2px solid black",
              "&:hover": { backgroundColor: "#FFB07A" },
              color: "black",
              fontFamily: "Montserrat",
              fontWeight: 600,
              textTransform: "none",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              minWidth: 0,
            }}
            onClick={() => postData(false)}
            title="Save Floorplan"
          >
            <SaveRoundedIcon sx={{ fontSize: 20 }} />
            <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
              Save
            </Box>
          </Button>
          <Button
            sx={{
              backgroundColor: "#FBE5C6",
              padding: { xs: ".4rem .6rem", md: ".5rem 1rem" },
              borderRadius: "6px",
              border: "2px solid black",
              "&:hover": { backgroundColor: "#FFF4E5" },
              color: "black",
              fontFamily: "Montserrat",
              fontWeight: 600,
              textTransform: "none",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              minWidth: 0,
            }}
            onClick={handleExportOpen}
            title="Export"
          >
            <FileDownloadRoundedIcon sx={{ fontSize: 20 }} />
            <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
              Export
            </Box>
            <ExpandMoreIcon sx={{ fontSize: 18, marginLeft: "2px", display: { xs: "none", sm: "inline-block" } }} />
          </Button>
          <Menu
            anchorEl={exportAnchorEl}
            open={Boolean(exportAnchorEl)}
            onClose={handleExportClose}
          >
            <MenuItem
              onClick={() => {
                handleExportClose();
                exportjson();
              }}
            >
              Export as JSON
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleExportClose();
                savePdf();
              }}
            >
              Export as PDF
            </MenuItem>
          </Menu>
          <GuidedTour
            steps={outputEditorTourSteps}
            tourId="output-editor"
            autoStart={false}
            renderTrigger={(startTour) => (
              <Button
                sx={{
                  backgroundColor: "#FBE5C6",
                  padding: { xs: ".4rem .6rem", md: ".5rem 1rem" },
                  borderRadius: "6px",
                  border: "2px solid black",
                  "&:hover": { backgroundColor: "#FFF4E5" },
                  color: "black",
                  fontFamily: "Montserrat",
                  fontWeight: 600,
                  textTransform: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  minWidth: 0,
                }}
                onClick={startTour}
                title="Guided Tutorial"
              >
                <Box component="span" sx={{ fontSize: 18, mr: { xs: 0, sm: "4px" } }}>
                  💡
                </Box>
                <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                  Tutorial
                </Box>
              </Button>
            )}
          />
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default TopNav;
